import { createSlice } from '@reduxjs/toolkit'

/**
 * What the QR label screen carries about a chosen product.
 *
 * Deliberately the WHOLE subset a label needs, not just the id. Two reasons:
 *
 *  - the print page then needs no request at all. It already holds everything
 *    that goes on a label, so opening it is instant and works offline;
 *  - it closes a race. Storing ids and re-fetching means a product whose code
 *    was edited between selecting and printing prints under its new code onto
 *    boxes that are already labelled with the old one.
 *
 * `name_bn` travels too, because the caption may be printed in Bangla and the
 * list has already paid for it.
 */
function labelFields(product) {
    return {
        id: product.id,
        code: product.code,
        name: product.name,
        name_bn: product.name_bn,
    }
}

export const ProductSlice = createSlice({
    name: 'product',
    initialState: {
        productList: [],
        productGradetList: [],
        /**
         * Products ticked for QR label printing.
         *
         * An array rather than a Set: redux state has to be serialisable, and
         * RTK's checker complains about a Set the moment it lands in the store.
         * Selections are tens of items, not thousands, so the linear lookups
         * below cost nothing measurable.
         *
         * Lives here rather than in the print page's own state because the
         * ticking happens on the product LIST and the printing happens on
         * another route — component state would not survive the navigation
         * that is the whole point of the feature.
         */
        qrSelection: [],
        formValue: {
            "name": "",
            "category_id": "",
            "sub_category_id": "",
            "product_grade_id": "",
            "code": "",
            "supplier_id": "",
            "image": null,
            "sqf_per_tiles": "",
            "sqf_per_box": "",
            "note": "",
            "description": "",
            "purchase_price": 0,
            "sales_price": 0,
            "has_offer_price": 0,
            "offer_price": 0,
        }
    },

    reducers: {

        onChangeProductFormInput: (state, action) => {
            state.formValue[`${action.payload.name}`] = action.payload.value;
        },
        resetProductFormValue: (state, action) => {
            Object.keys(state.formValue).forEach((i) => state.formValue[i] = "");
        },
        setProductList: (state, action) => {
            state.productList = action.payload
        },
        setProductGradeList: (state, action) => {
            state.productGradetList = action.payload
        },
        toggleManageOffer: (state, action) => {
            state.formValue[`${action.payload.name}`] = !state.formValue[`${action.payload.name}`]
        },

        /**
         * Tick or untick one product. `{ product, selected }`.
         *
         * Idempotent on purpose — ticking something already ticked must not add
         * it twice, or the sheet prints two labels where one was asked for.
         */
        toggleQrSelection: (state, action) => {
            const { product, selected } = action.payload
            const at = state.qrSelection.findIndex((p) => p.id === product.id)
            if (selected && at === -1) state.qrSelection.push(labelFields(product))
            if (!selected && at !== -1) state.qrSelection.splice(at, 1)
        },

        /**
         * The header checkbox: tick or untick every product on the current
         * page. `{ products, selected }`.
         *
         * "The page" is what the caller passes, which after a search is the
         * filtered page. That is the honest reading of a control sitting at the
         * top of the rows you can see.
         */
        toggleQrSelectionPage: (state, action) => {
            const { products, selected } = action.payload
            products.forEach((product) => {
                const at = state.qrSelection.findIndex((p) => p.id === product.id)
                if (selected && at === -1) state.qrSelection.push(labelFields(product))
                if (!selected && at !== -1) state.qrSelection.splice(at, 1)
            })
        },

        /**
         * Start again.
         *
         * Called on Clear, after a sheet is downloaded, and when the product
         * section is left. A selection that survives quietly into next week is
         * how somebody prints a sheet of labels nobody asked for.
         */
        clearQrSelection: (state) => {
            state.qrSelection = []
        },

        /**
         * Select exactly one product, replacing whatever was ticked.
         *
         * The per-row "Print QR" action. Relabelling one product is far more
         * common than a batch, and making that path go through a checkbox, a
         * scroll and a bulk bar is friction on the case that happens most.
         */
        setQrSelectionToOne: (state, action) => {
            state.qrSelection = [labelFields(action.payload)]
        },

    },
})

export const {
    onChangeProductFormInput,
    resetProductFormValue,
    setProductList,
    setProductGradeList,
    toggleManageOffer,
    toggleQrSelection,
    toggleQrSelectionPage,
    clearQrSelection,
    setQrSelectionToOne,
} = ProductSlice.actions

export default ProductSlice.reducer